import { useEffect, useState } from "react";
import { Field, Input, Label, Radio, RadioGroup } from "@headlessui/react";
import { PaperClipIcon } from "@heroicons/react/24/outline";
import AlertMessage, { AlertLevel } from "../../components/AlertMessage";
import {
	StimulusMaterialType,
	StimulusMaterialSaveModel,
	StimulusMaterialEditModel,
} from "./stimulusMaterialApiSlice";

const MAX_FILE_SIZE_BYTES = 5242880;

const typeOptions = [StimulusMaterialType.File, StimulusMaterialType.Url];

interface Props {
	promptId?: number;
	editableStimulusMaterial?: StimulusMaterialEditModel;
	errorMessage: string;
	updateStimMaterialModel: (model: StimulusMaterialSaveModel) => void;
	disableConfirm: (disable: boolean) => void;
}

export default function StimulusMaterialForm(props: Props) {
	const [stimMaterial, setStimMaterial] = useState<StimulusMaterialSaveModel>();
	const [type, setType] = useState<StimulusMaterialType>(
		props.editableStimulusMaterial?.stimulusMaterialType ?? typeOptions[0]
	);
	const [label, setLabel] = useState(props.editableStimulusMaterial?.label ?? "");
	const [url, setUrl] = useState(props.editableStimulusMaterial?.url ?? "");
	const [file, setFile] = useState<FileList | null>();
	const [fileName, setFileName] = useState(props.editableStimulusMaterial?.fileName ?? "");
	const [useExistingFile, setUseExistingFile] = useState(
		props.editableStimulusMaterial?.fileName != undefined
	);
	const [agreeToTerms, setAgreeToTerms] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");

	useEffect(() => {
		setErrorMessage(props.errorMessage);
	}, [props.errorMessage]);

	useEffect(() => {
		if (stimMaterial) props.updateStimMaterialModel(stimMaterial);
	}, [stimMaterial]);

	useEffect(() => {
		if (
			!label ||
			(type === StimulusMaterialType.Url && !url) ||
			(type === StimulusMaterialType.File &&
				!useExistingFile &&
				(!file || (file && file.length == 0))) ||
			(file && file.length > 0 && file[0].size > MAX_FILE_SIZE_BYTES) ||
			!agreeToTerms
		) {
			props.disableConfirm(true);
		} else {
			props.disableConfirm(false);
		}

		const saveModel: StimulusMaterialSaveModel = {
			editModel: {
				id: props.editableStimulusMaterial?.id ?? 0,
				promptId: props.promptId ?? 0,
				label,
				stimulusMaterialType: type,
				url,
			},
			files: file ?? undefined,
			editing: props.editableStimulusMaterial != undefined,
		};
		setStimMaterial(saveModel);
	}, [label, type, file, url, agreeToTerms, useExistingFile]);

	useEffect(() => {
		if (file && file.length > 0) {
			setFileName(file[0].name);
			setUseExistingFile(false);
			if (!label && file) {
				setLabel(file[0].name);
			}

			if (file && file[0].size > MAX_FILE_SIZE_BYTES) {
				setErrorMessage("File too large. File size cannot be larger than 5 MB.");
			} else {
				setErrorMessage("");
			}
		} else if (
			props.editableStimulusMaterial?.stimulusMaterialType === StimulusMaterialType.File &&
			!file
		) {
			setFileName(props.editableStimulusMaterial?.fileName ?? "");
		} else {
			setFileName("");
		}
	}, [file]);

	return (
		<div>
			{errorMessage && (
				<AlertMessage
					className="text-sm mt-4 py-2 !px-4"
					type={AlertLevel.Error}
					message={errorMessage}
					showIcon={true}
				/>
			)}
			<Field className="mt-2">
				<Label htmlFor="label" className="font-semibold text-sm">
					Label
				</Label>
				<Input
					id="labelTextbox"
					name="label"
					type="text"
					value={label}
					className="block w-full mt-1 h-11 rounded-lg border-none text-gray-900 text-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600"
					onChange={(e) => setLabel(e.target.value)}
				/>
			</Field>
			<Field className="mt-2">
				<Label htmlFor="type" className="font-semibold text-sm">
					Type
				</Label>
				<RadioGroup
					value={type}
					onChange={setType}
					className="mt-1 w-full grid grid-cols-2 gap-3">
					{typeOptions.map((option) => (
						<Radio
							key={option}
							value={option}
							className="cursor-pointer focus:outline-hidden flex items-center justify-center rounded-md bg-white p-2 text-sm font-semibold text-gray-900 ring-1 ring-gray-300 hover:bg-gray-50 data-[checked]:bg-primary-600 data-[checked]:text-white data-[checked]:ring-0 data-[focus]:data-[checked]:ring-2 data-[focus]:ring-2 data-[focus]:ring-primary-600 data-[focus]:ring-offset-2 data-[checked]:hover:bg-primary-700 sm:flex-1 [&:not([data-focus])]:[&:not([data-checked])]:ring-inset">
							{option === StimulusMaterialType.File ? "File" : "URL"}
						</Radio>
					))}
				</RadioGroup>
			</Field>
			<Field className="mt-2">
				<Label htmlFor="attachment" className="font-semibold text-sm">
					{type === StimulusMaterialType.File ? "File" : "URL"}
				</Label>
				{type === StimulusMaterialType.File ? (
					<div className="flex h-11 w-full mt-1">
						<Input
							id="attachment"
							name="attachment"
							type="text"
							placeholder="No file chosen"
							value={fileName}
							className="block rounded-none grow rounded-l-md border-0 p-2 text-gray-900 text-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600"
							disabled={true}
						/>
						<button className="btn btn-neutral !rounded-l-none w-36 ">
							<label className="flex hover:cursor-pointer">
								<PaperClipIcon className="size-5" aria-hidden="true" />
								<span className="flex pl-2 items-center">Choose File</span>
								<Input
									id="attachment"
									name="attachment"
									type="file"
									multiple={false}
									accept="image/*, .doc, .docx, .pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/msword"
									className="sr-only"
									onChange={(e) => setFile(e.target.files)}
								/>
							</label>
						</button>
					</div>
				) : (
					<Input
						id="attachment"
						name="attachment"
						type="text"
						placeholder="https://"
						value={url}
						className="block w-full h-11 mt-1 rounded-lg border-none text-gray-900 text-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600"
						onChange={(e) => setUrl(e.target.value)}
					/>
				)}
			</Field>
			<Field className="mt-3">
				<div className="flex items-center ml-2 gap-x-4">
					<Input
						id="termsCheckbox"
						type="checkbox"
						className="size-4 rounded-sm border-2 border-gray-300 text-primary-600 focus:ring-primary-600 hover:cursor-pointer"
						onChange={() => setAgreeToTerms(!agreeToTerms)}
						checked={agreeToTerms}
					/>
					<span className="text-xs">
						I agree to the following terms of use for uploading text, images, links or
						other material ("Content") to this Measurement Incorporated website.
					</span>
				</div>
				<StimulusMaterialTerms />
			</Field>
		</div>
	);
}

function StimulusMaterialTerms() {
	return (
		<div id="AcceptableUsePolicy" className="h-36 overflow-auto border-2 text-xs p-2 mt-2">
			<p>
				By uploading Content I accept full responsibility for it and release Measurement
				Incorporated from any and all liabilities and responsibilities related to it.
			</p>
			<p className="mt-2">By uploading Content, I certify that the Content</p>
			<ul className="list-disc m-1 ml-5 p-2">
				<li>
					is in the public domain or otherwise free from copyrights and trademarks held by
					others, or
				</li>
				<li>
					is protected by copyrights or trademarks for which I have been granted
					documented permission allowing its use for this purpose, or
				</li>
				<li>
					is owned by me as the copyright holder and is hereby released under the Creative
					Commons CC0 public domain dedication{" "}
					<a href="https://creativecommons.org/publicdomain/zero/1.0/" target="_blank">
						found here
					</a>
					.
				</li>
			</ul>
			<p>I further certify that the Content:</p>
			<ul className="list-disc m-1 ml-5 p-2">
				<li>Does not contain profanity words, or imply profanity by using gestures,</li>
				<li>Does not contain adult content or &ldquo;not safe for work&rdquo; images,</li>
				<li>Does not depict hatred or imply hatred to any group or ethnicity,</li>
				<li>Does not discriminate in any way,</li>
				<li>Does not disclose personally identifiable information about students, and,</li>
				<li>Does not violate any US laws including copyrights.</li>
			</ul>
			<p>
				Measurement Incorporated reserves the right to delete any content deemed
				unacceptable, and reserves the right to determine what content is acceptable even if
				you uploaded content that you believe complies with the upload policy. Under the
				terms of the Digital Millennium Copyright Act (DMCA), Measurement Incorporated may
				delete infringing Content and reserves the right to terminate the use of the content
				upload feature from any user for repeated policy violations.
			</p>
		</div>
	);
}
